import { visit } from "unist-util-visit";
import { toHast } from "mdast-util-to-hast";

const COLOR_RE = new RegExp("\\\\color{(.*?)}(.*?)\\\\color{}");
const COLOR_OPEN_TAG_RE = new RegExp("\\\\color{(.*?)}");
const COLOR_CLOSE_TAG_RE = new RegExp("\\\\color{}");

const extractText = (string, start, end) => {
   const startLine = string.slice(0, start).split("\n");
   const endLine = string.slice(0, end).split("\n");
   //All this span business messes up spaces, so we need to create a span that spans the white space in it's own distinct div.
   if (string.slice(start, end).trim() === "") {
      return {
         type: "text",
         meta: "ECLSpace",
         value: string.slice(start, end),
         data: {
            hName: "span",
            hProperties: {},
            hChildren: [{ type: "text", value: string.slice(start, end) }],
         },
      };
   }
   return {
      type: "text",
      value: string.slice(start, end),
      position: {
         start: {
            line: startLine.length,
            column: startLine[startLine.length - 1].length + 1,
         },
         end: {
            line: endLine.length,
            column: endLine[endLine.length - 1].length + 1,
         },
      },
   };
};

export default function ColorPlugin() {
   function transformer(tree) {
      visit(tree, (node, position, parent) => {
         if (node.type === "text") {
            //Skip any ECLColor or ECLSpace node for parsing (As they've already been parsed)
            if (node.meta !== "ECLColor" && node.meta !== "ECLSpace") {
               const tempNodes = [];
               let lastIndex = 0;
               let match;
               //If we have a match
               if ((match = COLOR_RE.exec(node.value)) !== null) {
                  //get match values (see regex above)
                  const value = match[0];
                  const colorValue = match[1];
                  const textValue = match[2];
                  //if the match starts after zero index, then extract the leading text as it's own node
                  //and push to temp node list
                  if (match.index !== lastIndex) {
                     const extracted = extractText(
                        node.value,
                        lastIndex,
                        match.index,
                     );
                     tempNodes.push(extracted);
                  }
                  //create a Markdown node: https://github.com/rhysd/remark-emoji/blob/master/index.js
                  //see note about undocumented node functionality.  Not sure if this is remark specific,
                  //how it mixes with rehype, etc.
                  tempNodes.push({
                     type: "text",
                     meta: "ECLColor",
                     value: textValue,
                     data: {
                        hName: "span",
                        hProperties: {
                           style: "color:" + colorValue,
                           className: ["color-span"],
                        },
                        hChildren: [{ type: "text", value: textValue }],
                     },
                  });
                  lastIndex = match.index + value.length;
               }
               //if there's more text after the color tag, extract it and push to the temp node list
               if (lastIndex !== node.value.length) {
                  const text = extractText(
                     node.value,
                     lastIndex,
                     node.value.length,
                  );
                  tempNodes.push(text);
               }
               //Add the temp nodes into the parent node.
               const last = parent.children.slice(position + 1);
               parent.children = parent.children.slice(0, position);
               parent.children = parent.children.concat(tempNodes);
               parent.children = parent.children.concat(last);
            }
            //Check for open color tags in text nodes (This means there's elements embedded in the color tag)
            let openTagMatch;
            //assign open tag match the open tag regex (Make sure that we're not grabbing a full color tag though)
            if (
               (openTagMatch = COLOR_OPEN_TAG_RE.exec(node.value)) !== null &&
               COLOR_RE.exec(node.value) === null
            ) {
               // create a colored span element, with associated style and classname, and an empty list for children
               let colorSpan = {
                  type: "element",
                  meta: "ECLColor",
                  value: "",
                  data: {
                     hName: "span",
                     hProperties: {
                        style: "color:" + openTagMatch[1],
                        className: ["color-span"],
                     },
                     hChildren: [],
                  },
               };
               //Iterate through associated sibling elements, looking for the color's closing tag.
               let lastIndex;
               for (let i = position; i < parent.children.length; i++) {
                  if (
                     COLOR_CLOSE_TAG_RE.exec(parent.children[i].value) === null
                  ) {
                     //First element is always the color open tag, so strip that out
                     if (i === position) {
                        parent.children[i].value = parent.children[
                           i
                        ].value.replace(COLOR_OPEN_TAG_RE, "");
                     }
                     //add all elements to the hChildren of the colorSpan element, converting mdAST to hAST
                     colorSpan.data.hChildren.push(toHast(parent.children[i]));
                     continue;
                  }
                  //remove the color close tag from the final node
                  parent.children[i].value = parent.children[i].value.replace(
                     COLOR_CLOSE_TAG_RE,
                     "",
                  );
                  colorSpan.data.hChildren.push(toHast(parent.children[i]));
                  lastIndex = i;
               }
               //If last index is a real number (and no one left a color tag unclosed), then edit the
               //parent node to include the embedded color span
               if (lastIndex) {
                  const last = parent.children.slice(lastIndex + 1);
                  parent.children = parent.children.slice(0, position);
                  parent.children = parent.children.concat(colorSpan);
                  parent.children = parent.children.concat(last);
               }
            }
         }
      });
   }

   return transformer;
}
